import React, { useState } from 'react';
import type { Link } from '@helpcenter/analytics-components';
import type { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { EngagementMessages, EngagementMessagesTarget, HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@helpcenter/constants';
import { EngagementCoordinationClient } from '@helpcenter/engagement-coordination-client';
import { isMoveToManagementRouterEnabled } from '@helpcenter/feature-flags';
import { getHelpCenterAri } from '@helpcenter/meta';
import { RenderNextAnimationFrame } from '@helpcenter/render-next-animation-frame';
import { VaOnboardingSpotlight } from '@helpcenter/va-reporting-onboarding';
import { FormattedMessage } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { CustomItem } from '@atlaskit/menu';
import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri } from '@atlassian/ari';
import messages from './messages';

interface VirtualAgentMenuProps {
    onClose: (actionSubjectId?: string) => void;
    component: typeof Link | typeof HtmlAnchor;
}

export const VirtualAgentMenu = ({ onClose, component }: VirtualAgentMenuProps) => {
    di(VaOnboardingSpotlight, RenderNextAnimationFrame, EngagementCoordinationClient, getHelpCenterAri);
    const [showVaReportingOnboardingSpotlight, setShowVaReportingOnboardingSpotlight] = useState(true);
    const { helpCenterId } = HelpHelpCenterAri.parse(getHelpCenterAri());

    const handleClick = () => onClose('navBarVirtualAgent');

    return (
        <>
            <RenderNextAnimationFrame>
                {() => (
                    <SpotlightTarget
                        name={EngagementMessagesTarget.REPORTING_ONBOARDING_SPOTLIGHT}
                        key="navBarVirtualAgent"
                    >
                        <CustomItem
                            key={`customize-menu_navBarVirtualAgent`}
                            component={component}
                            {...(isMoveToManagementRouterEnabled() && {
                                href: `${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/virtual-agent`,
                            })}
                            {...(!isMoveToManagementRouterEnabled() && { to: '/virtual-agent' })}
                            actionSubjectId="navBarVirtualAgent"
                            onClick={handleClick}
                            testId="virtual-agent-link"
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                            className="cv-exclude-from-customisation"
                        >
                            <FormattedMessage {...messages.virtualServiceAgent} />
                        </CustomItem>
                    </SpotlightTarget>
                )}
            </RenderNextAnimationFrame>
            {showVaReportingOnboardingSpotlight && (
                <SpotlightTransition>
                    <EngagementCoordinationClient
                        messageId={EngagementMessages.ONBOARD_VA_REPORTING}
                        key={EngagementMessagesTarget.REPORTING_ONBOARDING_SPOTLIGHT}
                    >
                        <VaOnboardingSpotlight
                            setShowVaReportingOnboardingSpotlight={setShowVaReportingOnboardingSpotlight}
                        />
                    </EngagementCoordinationClient>
                </SpotlightTransition>
            )}
        </>
    );
};
